import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { loadArtworkById, loadAllArtworks, loadEmotionData } from "@/lib/static-data";


interface GraphNode {
  id: string;
  name: string;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface GraphLink {
  source: string | GraphNode;
  target: string | GraphNode;
}

interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

interface EmotionData {
  emotion: string;
  value: number;
}

interface KnowledgeGraphProps {
  artworkId: number;
  onNodeClick?: (nodeId: string) => void;
  onNodeHover?: (
    emotionData: EmotionData[] | null,
    artworkTitle?: string,
    artworkId?: number | null,
  ) => void;
}

export default function KnowledgeGraph({
  artworkId,
  onNodeClick,
  onNodeHover,
}: KnowledgeGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emotionDataMap, setEmotionDataMap] = useState<
    Map<number, EmotionData[]>
  >(new Map());

  // Load emotion data on component mount
  useEffect(() => {
    const initEmotionData = async () => {
      const emotionMap = await loadEmotionData();
      setEmotionDataMap(emotionMap);
      console.log("Loaded emotion data for", emotionMap.size, "artworks");
    };
    initEmotionData();
  }, []);

  const loadGraphData = async () => {
    try {
      setIsLoading(true);
      console.log("Loading graph data for artwork:", artworkId);

      // Check if we have preloaded data with graph connections
      const preloadedScript = document.getElementById("preloaded-data");
      if (!preloadedScript || !preloadedScript.textContent) {
        console.log(
          "No preloaded data found, trying graph database service...",
        );

        // Try to load from static data files for development
        try {
          const detailedArtwork = await loadArtworkById(artworkId);

          if (
            detailedArtwork &&
            detailedArtwork.graph &&
            Array.isArray(detailedArtwork.graph)
          ) {
            console.log(
              "Graph artwork IDs from static data:",
              detailedArtwork.graph,
            );
            await createGraphFromData(detailedArtwork, detailedArtwork.graph);
          } else {
            console.log("No graph data found for artwork", artworkId);
            setGraphData({ nodes: [], links: [] });
          }
        } catch (err) {
          console.error("Error loading from static data:", err);
          setGraphData({ nodes: [], links: [] });
        }
        return;
      }

      const preloadedData = JSON.parse(preloadedScript.textContent);
      console.log("Preloaded data:", preloadedData);

      if (
        !preloadedData.detailedArtwork ||
        !preloadedData.detailedArtwork.graph
      ) {
        console.log("No graph data found in preloaded artwork");
        setGraphData({ nodes: [], links: [] });
        return;
      }

      const graphArtworkIds = preloadedData.detailedArtwork.graph;
      console.log("Graph artwork IDs:", graphArtworkIds);

      if (!Array.isArray(graphArtworkIds) || graphArtworkIds.length === 0) {
        console.log("Graph data is empty or invalid");
        setGraphData({ nodes: [], links: [] });
        return;
      }

      await createGraphFromData(preloadedData.detailedArtwork, graphArtworkIds);
    } catch (err) {
      console.error("Error processing graph data:", err);
      setGraphData({ nodes: [], links: [] });
    } finally {
      setIsLoading(false);
    }
  };

  const createGraphFromData = async (
    artworkData: any,
    graphArtworkIds: number[],
  ) => {
    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];

    // Add the current artwork as the central node
    nodes.push({
      id: `artwork_${artworkId}`,
      name: artworkData.title || "Current Artwork",
    });

    // Load all artworks to get titles for connected nodes
    console.log("Loading all artworks to get titles for connected nodes...");
    const allArtworks = await loadAllArtworks();

    // Create a lookup map for quick artwork title retrieval
    // The artwork IDs in the graph should map to the actual artwork IDs in the data
    const artworkMap = new Map();
    allArtworks.forEach((artwork, index) => {
      // Check if artwork has an ID field, otherwise use index + 1
      const id = artwork.id || index + 1;
      artworkMap.set(id, artwork.title || artwork.name || "Unknown Title");
    });

    console.log("Created artwork map with", artworkMap.size, "entries");
    console.log(
      "Sample mappings:",
      Array.from(artworkMap.entries()).slice(0, 5),
    );

    // Add connected artworks as nodes and create links
    // Only include artworks that exist in our dataset to avoid showing "Artwork ID" placeholders
    graphArtworkIds.forEach((connectedId: number) => {
      const artworkTitle = artworkMap.get(connectedId);
      
      // Skip this connection if artwork doesn't exist in our dataset
      if (!artworkTitle) {
        console.log(`Skipping ID ${connectedId}: not found in artwork dataset`);
        return;
      }
      
      console.log(`Looking up ID ${connectedId}: found "${artworkTitle}"`);

      nodes.push({
        id: `artwork_${connectedId}`,
        name:
          artworkTitle.length > 20
            ? artworkTitle.slice(0, 20) + "..."
            : artworkTitle,
      });

      links.push({
        source: `artwork_${artworkId}`,
        target: `artwork_${connectedId}`,
      });
    });

    console.log(
      "Created nodes with real titles:",
      nodes.length,
      "links:",
      links.length,
    );
    console.log(
      "Sample node titles:",
      nodes.slice(0, 3).map((n) => n.name),
    );

    if (nodes.length > 1) {
      setGraphData({ nodes, links });
    } else {
      setGraphData({ nodes: [], links: [] });
    }
  };

  useEffect(() => {
    loadGraphData();
  }, [artworkId]);

  useEffect(() => {
    if (!graphData || !svgRef.current || graphData.nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const width = 600;
    const height = 400;

    const simulation = d3
      .forceSimulation<GraphNode>(graphData.nodes)
      .force(
        "link",
        d3
          .forceLink<GraphNode, GraphLink>(graphData.links)
          .id((d) => d.id)
          .distance(80),
      )
      .force("charge", d3.forceManyBody().strength(-300))
      .force("center", d3.forceCenter(width / 2, height / 2));

    // Create links
    const links = svg
      .append("g")
      .selectAll("line")
      .data(graphData.links)
      .enter()
      .append("line")
      .attr("stroke", "#d1d5db")
      .attr("stroke-width", 1.5)
      .attr("opacity", 0.6);

    // Create nodes
    const nodes = svg
      .append("g")
      .selectAll("circle")
      .data(graphData.nodes)
      .enter()
      .append("circle")
      .attr("r", 15)
      .attr("fill", "#4f46e5")
      .attr("stroke", "#f9fafb")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        if (onNodeClick && d.id.startsWith("artwork_")) {
          const artworkId = parseInt(d.id.replace("artwork_", ""));
          if (!isNaN(artworkId)) {
            onNodeClick(artworkId.toString());
          }
        }
      })
      .on("mouseenter", (event, d) => {
        if (onNodeHover && d.id.startsWith("artwork_")) {
          const artworkId = parseInt(d.id.replace("artwork_", ""));
          if (!isNaN(artworkId)) {
            const emotionData = emotionDataMap.get(artworkId);
            if (emotionData) {
              onNodeHover(emotionData, d.name, artworkId);
            }
          }
        }
      })
      .on("mouseleave", () => {
        if (onNodeHover) {
          onNodeHover(null, undefined, null);
        }
      });

    // Add labels
    const labels = svg
      .append("g")
      .selectAll("text")
      .data(graphData.nodes)
      .enter()
      .append("text")
      .text((d) => (d.name.length > 15 ? d.name.slice(0, 15) + "..." : d.name))
      .style("font-size", "12px")
      .style("text-anchor", "middle")
      .attr("dy", 25);

    // Update positions on tick
    simulation.on("tick", () => {
      links
        .attr("x1", (d) => (d.source as GraphNode).x!)
        .attr("y1", (d) => (d.source as GraphNode).y!)
        .attr("x2", (d) => (d.target as GraphNode).x!)
        .attr("y2", (d) => (d.target as GraphNode).y!);

      nodes.attr("cx", (d) => d.x!).attr("cy", (d) => d.y!);

      labels.attr("x", (d) => d.x!).attr("y", (d) => d.y!);
    });

    return () => {
      simulation.stop();
    };
  }, [graphData, onNodeClick]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!graphData || graphData.nodes.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p>No emotion similarities found</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="border rounded-lg bg-gray-50">
        <svg
          ref={svgRef}
          width="600"
          height="400"
          viewBox="0 0 600 400"
          className="w-full"
        />
      </div>
    </div>
  );
}
